import { Loader2, Send } from 'lucide-react';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createNote, getNotes, type PaginatedNotes } from '@/api/notes';
import type { SupplierRelationship } from '@/api/suppliers';
import { formatDate } from '@/lib/utils';
import { Textarea } from './ui/textarea';

interface SupplierNotesPanelProps {
  open: boolean;
  /** Pre-resolved entity ID for non-internal users (supplier.relationship_id ?? supplier.id). */
  nonInternalEntityId: string | null;
  isInternal: boolean;
  /** CLIENT_SUPPLIER relationships where the supplier is the `to` party (direction === 'from'). */
  clientRelationships: SupplierRelationship[];
  /** True while the parent is loading detailed supplier data (used for the client picker spinner). */
  clientsLoading: boolean;
  supplierLabel: string;
  scrollContainerRef: React.RefObject<HTMLDivElement>;
}

export const SupplierNotesPanel: React.FC<SupplierNotesPanelProps> = ({
  open,
  nonInternalEntityId,
  isInternal,
  clientRelationships,
  clientsLoading,
  supplierLabel,
  scrollContainerRef,
}) => {
  const [notes, setNotes] = useState<PaginatedNotes | null>(null);
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesPage, setNotesPage] = useState(1);
  const [newNote, setNewNote] = useState('');
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [selectedClientRelationshipId, setSelectedClientRelationshipId] = useState<string | null>(
    null
  );
  const [clientsPage, setClientsPage] = useState(1);
  const savedScrollTopRef = useRef<number>(0);

  const notesEntityId = useMemo(() => {
    if (isInternal) return selectedClientRelationshipId;
    return nonInternalEntityId;
  }, [isInternal, selectedClientRelationshipId, nonInternalEntityId]);

  // Reset all state when the dialog closes
  useEffect(() => {
    if (!open) {
      setNotes(null);
      setNotesPage(1);
      setNewNote('');
      setNoteError(null);
      setSelectedClientRelationshipId(null);
      setClientsPage(1);
    }
  }, [open]);

  // Auto-select when there is exactly one client relationship
  useEffect(() => {
    if (isInternal && clientRelationships.length === 1) {
      setSelectedClientRelationshipId(clientRelationships[0].id);
    }
  }, [isInternal, clientRelationships]);

  // Restore scroll position after notes finish loading
  useEffect(() => {
    if (!notesLoading && savedScrollTopRef.current > 0) {
      if (scrollContainerRef.current) {
        scrollContainerRef.current.scrollTop = savedScrollTopRef.current;
      }
      savedScrollTopRef.current = 0;
    }
  }, [notesLoading, scrollContainerRef]);

  useEffect(() => {
    if (open && notesEntityId) {
      setNotesLoading(true);
      getNotes('PARTY_RELATIONSHIP', notesEntityId, notesPage, 5)
        .then((data) => setNotes(data))
        .catch(() => {
          /* silently ignore */
        })
        .finally(() => setNotesLoading(false));
    } else {
      setNotes(null);
      setNotesPage(1);
    }
  }, [open, notesEntityId, notesPage]);

  const handleSubmitNote = useCallback(async () => {
    if (!newNote.trim() || !notesEntityId) return;
    setNoteSubmitting(true);
    setNoteError(null);
    try {
      await createNote('PARTY_RELATIONSHIP', notesEntityId, newNote.trim());
      setNewNote('');
      setNotesPage(1);
      const data = await getNotes('PARTY_RELATIONSHIP', notesEntityId, 1, 5);
      setNotes(data);
      window.dispatchEvent(new CustomEvent('steerfi:supplier-notes-changed'));
    } catch {
      setNoteError('Failed to save note');
    } finally {
      setNoteSubmitting(false);
    }
  }, [newNote, notesEntityId]);

  const totalClientPages = Math.ceil(clientRelationships.length / 5);

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <h2 className="text-[11px] uppercase tracking-widest text-muted-foreground">Notes</h2>
        {notes && <span className="text-xs text-muted-foreground">{notes.total} notes</span>}
      </div>

      {/* Internal user with multiple clients: show picker or drill-in */}
      {isInternal && clientRelationships.length > 1 && !selectedClientRelationshipId ? (
        clientsLoading ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-1.5">
            {clientRelationships.slice((clientsPage - 1) * 5, clientsPage * 5).map((rel) => (
              <button
                key={rel.id}
                type="button"
                onClick={() => setSelectedClientRelationshipId(rel.id)}
                className="flex w-full items-center justify-between gap-2 rounded-lg border px-3.5 py-2.5 text-sm hover:bg-muted/50"
              >
                <span className="flex min-w-0 flex-1 items-center gap-1.5 text-left">
                  <span className="min-w-0 truncate">{rel.related_party_name}</span>
                  {(rel.notes_count ?? 0) > 0 && (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minWidth: '18px',
                        height: '18px',
                        padding: '0 5px',
                        borderRadius: '999px',
                        fontSize: '11px',
                        fontWeight: 500,
                        lineHeight: 1,
                        backgroundColor: 'var(--foreground)',
                        color: 'var(--background)',
                      }}
                    >
                      {rel.notes_count}
                    </span>
                  )}
                </span>
                <span className="shrink-0 text-muted-foreground">→</span>
              </button>
            ))}
            {totalClientPages > 1 && (
              <div className="flex items-center justify-between pt-1">
                <button
                  type="button"
                  disabled={clientsPage <= 1}
                  onClick={() => setClientsPage((p) => Math.max(1, p - 1))}
                  className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-40"
                >
                  ← Previous
                </button>
                <span className="text-xs text-muted-foreground">
                  Page {clientsPage} of {totalClientPages}
                </span>
                <button
                  type="button"
                  disabled={clientsPage >= totalClientPages}
                  onClick={() => setClientsPage((p) => p + 1)}
                  className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-40"
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        )
      ) : (
        <>
          {/* Back button when an internal user has drilled into a specific client */}
          {isInternal && clientRelationships.length > 1 && selectedClientRelationshipId && (
            <button
              type="button"
              onClick={() => {
                setSelectedClientRelationshipId(null);
                setNotes(null);
                setNotesPage(1);
                setClientsPage(1);
              }}
              className="mb-3 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              ← Back to clients
            </button>
          )}

          {/* Notes list */}
          {notesLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : notes && notes.data.length > 0 ? (
            <div className="mb-3 space-y-2">
              {notes.data.map((n) => (
                <div key={n.id} className="rounded-xl border p-3.5 text-sm">
                  <p className="whitespace-pre-wrap text-sm">{n.note}</p>
                  <p className="mt-1.5 text-xs text-muted-foreground">
                    {n.created_by_name ?? n.created_by_id ?? 'Unknown'}
                    {isInternal && n.created_by_party_name && (
                      <span className="ml-1 text-blue-600 dark:text-blue-400">
                        ({n.created_by_party_name})
                      </span>
                    )}
                    {' · '}
                    {formatDate(n.created_at)}
                  </p>
                </div>
              ))}
              {notes.totalPages > 1 && (
                <div className="flex items-center justify-between pt-1">
                  <button
                    type="button"
                    disabled={notes.page <= 1}
                    onClick={() => {
                      savedScrollTopRef.current = scrollContainerRef.current?.scrollTop ?? 0;
                      setNotesPage((p) => Math.max(1, p - 1));
                    }}
                    className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-40"
                  >
                    ← Previous
                  </button>
                  <span className="text-xs text-muted-foreground">
                    Page {notes.page} of {notes.totalPages}
                  </span>
                  <button
                    type="button"
                    disabled={notes.page >= notes.totalPages}
                    onClick={() => {
                      savedScrollTopRef.current = scrollContainerRef.current?.scrollTop ?? 0;
                      setNotesPage((p) => p + 1);
                    }}
                    className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-40"
                  >
                    Next →
                  </button>
                </div>
              )}
            </div>
          ) : !notesLoading ? (
            <p className="mb-3 py-2 text-sm text-muted-foreground">No notes associated yet.</p>
          ) : null}

          {/* New note input */}
          <div className="relative">
            <Textarea
              placeholder={`Add a note about this ${supplierLabel}...`}
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              className="min-h-[72px] resize-none pr-10 text-sm"
              disabled={noteSubmitting}
            />
            <button
              type="button"
              onClick={handleSubmitNote}
              disabled={!newNote.trim() || noteSubmitting}
              className="absolute bottom-2.5 right-2.5 rounded-md p-1 text-muted-foreground hover:text-foreground disabled:opacity-40"
              aria-label="Submit note"
            >
              {noteSubmitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" />
              )}
            </button>
          </div>
          {noteError && <p className="mt-1 text-xs text-destructive">{noteError}</p>}
        </>
      )}
    </div>
  );
};
